// The Summary breakdown: one sortable, searchable table of sales by a chosen dimension,
// with the comparison side by side. Clicking a name focuses the whole page on it.
import { useEffect, useMemo, useState } from "react";
import { canDrill } from "./drillDown";
import { changeText, count, materialTone, money, pct, shortRange } from "./format";
import "./SalesExplorer.css";

export const BREAKDOWN_DIMENSIONS = [
  { key: "product_category", label: "Category", noun: "category", search: "categories" },
  { key: "product", label: "Product", noun: "product", search: "products or SKUs" },
  { key: "customer", label: "Customer", noun: "customer", search: "customers" },
  { key: "salesman", label: "Salesman", noun: "salesman", search: "salesmen" },
  { key: "route", label: "Route", noun: "route", search: "routes" },
  { key: "vendor", label: "Vendor", noun: "vendor", search: "vendors" },
  { key: "account_type", label: "Account type", noun: "account type", search: "account types" },
];
const PAGE = 25;
const EMPTY = [];
const number = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};
const nameOf = (row) => row.name || "Unassigned";

function csvCell(value) {
  const text = value == null ? "" : String(value);
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

function downloadCsv(filename, lines) {
  if (typeof URL === "undefined" || typeof URL.createObjectURL !== "function") return;
  const blob = new Blob([lines.map((line) => line.map(csvCell).join(",")).join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

function SortHeader({ field, label, sort, onSort, className = "" }) {
  const active = sort.field === field;
  return <th scope="col" className={className} aria-sort={active ? (sort.direction === "asc" ? "ascending" : "descending") : "none"}>
    <button type="button" className="sa-sort" onClick={() => onSort(field)}>
      {label}<span className="sa-sort-icon" aria-hidden="true">{active ? (sort.direction === "asc" ? "▲" : "▼") : ""}</span>
    </button>
  </th>;
}

export default function SalesExplorer({ report, dimension: dimensionKey, onDimensionChange, movement = "all", onMovementChange, onDrill, onClose }) {
  const dimension = BREAKDOWN_DIMENSIONS.find((item) => item.key === dimensionKey) || BREAKDOWN_DIMENSIONS[0];
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState({ field: "sales", direction: "desc" });
  const [showAll, setShowAll] = useState(false);
  useEffect(() => { setQuery(""); setShowAll(false); }, [dimension.key]);

  const allRows = Array.isArray(report?.breakdowns?.[dimension.key]) ? report.breakdowns[dimension.key] : EMPTY;
  const adjustment = allRows.find((row) => row.key === "adjustment");
  const rows = useMemo(() => allRows.filter((row) => row.key !== "adjustment"), [allRows]);
  const hasComparison = Boolean(report?.comparison);
  const currentDates = shortRange(report?.date_range?.start_date, report?.date_range?.end_date);
  const previousDates = hasComparison ? shortRange(report.comparison.start_date, report.comparison.end_date) : "";
  const activeMovement = hasComparison ? movement : "all";
  const sortField = !hasComparison && ["previous_sales", "change_amount", "change_percent"].includes(sort.field) ? "sales" : sort.field;

  const sorted = useMemo(() => {
    const term = query.trim().toLocaleLowerCase();
    const direction = sort.direction === "asc" ? 1 : -1;
    return rows
      .filter((row) => !term || nameOf(row).toLocaleLowerCase().includes(term) || String(row.sku || "").toLocaleLowerCase().includes(term))
      .filter((row) => activeMovement === "all" || (activeMovement === "up" ? number(row.change_amount) > 0 : number(row.change_amount) < 0))
      .sort((a, b) => {
        if (sortField === "name") return direction * nameOf(a).localeCompare(nameOf(b));
        const aMissing = a[sortField] == null;
        const bMissing = b[sortField] == null;
        if (aMissing !== bMissing) return aMissing ? 1 : -1;
        return direction * (number(a[sortField]) - number(b[sortField])) || nameOf(a).localeCompare(nameOf(b));
      });
  }, [rows, query, activeMovement, sortField, sort.direction]);
  const visible = showAll ? sorted : sorted.slice(0, PAGE);
  const maxShare = Math.max(1, ...rows.map((row) => number(row.share_percent)));

  function handleSort(field) {
    setSort((current) => current.field === field
      ? { field, direction: current.direction === "asc" ? "desc" : "asc" }
      : { field, direction: field === "name" ? "asc" : "desc" });
  }

  function exportCsv() {
    const header = [dimension.label, ...(dimension.key === "product" ? ["SKU"] : []), `Sales ${currentDates}`,
      ...(hasComparison ? [`Sales ${previousDates}`, "Change", "Change %"] : []), "Units", "Share %"];
    const line = (row) => [nameOf(row), ...(dimension.key === "product" ? [row.sku || ""] : []), row.sales,
      ...(hasComparison ? [row.previous_sales, row.change_amount, row.change_percent ?? ""] : []), row.units ?? "", row.share_percent ?? ""];
    const start = report?.date_range?.start_date || "";
    const end = report?.date_range?.end_date || "";
    downloadCsv(`sales-by-${dimension.key.replace("_", "-")}-${start}-to-${end}.csv`,
      [header, ...sorted.map(line), ...(adjustment ? [line(adjustment)] : [])]);
  }

  return <section className="sa-breakdown" aria-labelledby="sa-breakdown-title">
    <div className="sa-breakdown-head">
      <div>
        <h2 id="sa-breakdown-title">Sales by {dimension.noun}</h2>
        <p>{count(rows.length)} {rows.length === 1 ? dimension.noun : dimension.search.replace(" or SKUs", "")} with sales {currentDates}{hasComparison ? `. Changes are against ${previousDates}.` : "."}</p>
      </div>
      {onClose && <button type="button" className="sa-breakdown-close" aria-label="Close breakdown" onClick={onClose}>
        <svg aria-hidden="true" width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M3 3l10 10M13 3L3 13" /></svg>
      </button>}
    </div>
    <div className="sa-breakdown-dimensions sa-seg" role="group" aria-label="Break down by">
      {BREAKDOWN_DIMENSIONS.map((item) => <button type="button" key={item.key} aria-pressed={item.key === dimension.key}
        className={item.key === dimension.key ? "is-selected" : ""} onClick={() => onDimensionChange?.(item.key)}>{item.label}</button>)}
    </div>
    <div className="sa-breakdown-tools">
      {hasComparison && <div className="sa-segmented" role="group" aria-label="Show">
        {[["all", "All"], ["up", "Grew"], ["down", "Fell"]].map(([value, label]) => <button type="button" key={value}
          aria-pressed={activeMovement === value} className={activeMovement === value ? "is-selected" : ""}
          onClick={() => onMovementChange?.(value)}>{label}</button>)}
      </div>}
      <input type="search" className="sa-breakdown-search" aria-label={`Search ${dimension.search}`} placeholder={`Search ${dimension.search}`}
        value={query} onChange={(event) => setQuery(event.target.value)} />
    </div>

    <div className="sa-table-wrap">
      <table className="sa-table" aria-label={`Sales by ${dimension.noun}`}>
        <thead><tr>
          <SortHeader field="name" label={dimension.label} sort={{ ...sort, field: sortField }} onSort={handleSort} className="sa-col-name" />
          <SortHeader field="sales" label={currentDates || "Sales"} sort={{ ...sort, field: sortField }} onSort={handleSort} />
          {hasComparison && <>
            <SortHeader field="previous_sales" label={previousDates} sort={{ ...sort, field: sortField }} onSort={handleSort} className="sa-col-wide" />
            <SortHeader field="change_amount" label="Change" sort={{ ...sort, field: sortField }} onSort={handleSort} />
          </>}
          <SortHeader field="units" label="Units" sort={{ ...sort, field: sortField }} onSort={handleSort} className="sa-col-wide" />
          <SortHeader field="share_percent" label="Share of sales" sort={{ ...sort, field: sortField }} onSort={handleSort} className="sa-col-share" />
        </tr></thead>
        <tbody>
          {visible.map((row) => {
            const label = <>{nameOf(row)}{row.sku && <small className="sa-sku">{row.sku}</small>}</>;
            return <tr key={row.key ?? `${row.id}-${row.name}`}>
              <th scope="row" className="sa-col-name">
                {onDrill && canDrill(row)
                  ? <button type="button" className="sa-row-name" onClick={() => onDrill(row)}>{label}</button>
                  : <span className="sa-row-name">{label}</span>}
              </th>
              <td>{money(row.sales)}</td>
              {hasComparison && <>
                <td className="sa-col-wide">{money(row.previous_sales)}</td>
                <td className={`sa-delta sa-delta--${materialTone(row.change_amount, row.change_percent)}`}>{changeText(row.change_amount, row.change_percent)}</td>
              </>}
              <td className="sa-col-wide">{row.units == null ? "—" : count(Math.round(number(row.units)))}</td>
              <td className="sa-col-share"><span className="sa-share">
                <span className="sa-share-track" aria-hidden="true"><span style={{ width: `${Math.max(0, number(row.share_percent)) / maxShare * 100}%` }} /></span>
                {pct(row.share_percent)}
              </span></td>
            </tr>;
          })}
        </tbody>
      </table>
      {!sorted.length && <p className="sa-table-empty">{rows.length ? "Nothing matches. Try another search or show all." : `No sales by ${dimension.noun} in these dates.`}</p>}
    </div>

    <div className="sa-breakdown-foot">
      <span>
        {sorted.length > PAGE && !showAll ? `Showing ${PAGE} of ${sorted.length}. ` : sorted.length !== rows.length ? `Showing ${sorted.length} of ${rows.length}. ` : ""}
        {onDrill && rows.some(canDrill) && (["customer", "product", "salesman"].includes(dimension.key)
          ? `Click a ${dimension.noun} for their details. ` : "Click a name to focus the page on it. ")}
        {adjustment && number(adjustment.sales) !== 0 && `Fees and adjustments not tied to a ${dimension.noun}: ${money(adjustment.sales, { cents: true })}.`}
      </span>
      <span className="sa-breakdown-actions">
        {sorted.length > PAGE && <button type="button" onClick={() => setShowAll(!showAll)}>{showAll ? `Show top ${PAGE}` : `Show all ${sorted.length}`}</button>}
        {rows.length > 0 && <button type="button" onClick={exportCsv}>Export CSV</button>}
      </span>
    </div>
  </section>;
}
